import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PopupOverlay, { Anchors } from '../Popovers/PopupOverlay';
import { useMap } from 'rwl-ui';
import { When } from 'react-if';
import { useMapContext } from '../../../hooks';

export type MarkerContentState = {
    isPopupOpen: boolean;
    isHovered: boolean;
};

interface BaseMarkerProps {
    position: google.maps.LatLngLiteral;
    markerId?: string;
    additionalMarkerIds?: string[];
    isExternallyHovered?: boolean;
    markerContent:
        | React.ReactNode
        | ((state: MarkerContentState) => React.ReactNode);
    popoverContent?: React.ReactNode;
    popoverAnchor?: Anchors;
    onMarkerClick?: () => void;
    onMarkerHover?: (state: HoverState) => void;
}

type MarkerStates = {
    isMarkerHovered: boolean;
    isPopupOpen: boolean;
};

export type HoverState = 'enter' | 'exit';

export const BaseMarker = ({
    position,
    markerId: externalMarkerId,
    additionalMarkerIds,
    isExternallyHovered,
    markerContent,
    popoverContent,
    popoverAnchor = 'bestCenter',
    onMarkerClick,
    onMarkerHover,
}: BaseMarkerProps) => {
    const { map } = useMap();
    const { openMarkerId, setOpenMarkerId, markerActionsRef } = useMapContext();
    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
        null
    );
    const markerContainerRef = useRef<HTMLDivElement>(
        document.createElement('div')
    );

    const [markerState, setMarkerState] = useState<MarkerStates>({
        isMarkerHovered: false,
        isPopupOpen: false,
    });

    // Single ref mirrors state for stale-closure reads in event handlers.
    // State remains the source of truth; the ref is just a reading mechanism.
    const markerStateRef = useRef<MarkerStates>(markerState);
    useEffect(() => {
        markerStateRef.current = markerState;
    }, [markerState]);

    // Ref always holds the latest openPopup so the registry callback is never stale
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const openPopupRef = useRef<() => void>(() => {});
    openPopupRef.current = () => {
        setOpenMarkerId(externalMarkerId);
        setMarkerState((prev) => ({ ...prev, isPopupOpen: true }));
    };

    // Register this marker's open callback so polygon clicks can invoke it directly
    useEffect(() => {
        if (!externalMarkerId) return;
        const allIds = [externalMarkerId, ...(additionalMarkerIds || [])];
        const uniqueIds = [...new Set(allIds)];
        for (const id of uniqueIds) {
            markerActionsRef.current.set(id, () => openPopupRef.current());
        }
        return () => {
            for (const id of uniqueIds) {
                markerActionsRef.current.delete(id);
            }
        };
    }, [externalMarkerId, additionalMarkerIds, markerActionsRef]);

    // Close this marker's popover when a different marker opens
    useEffect(() => {
        if (!markerStateRef.current.isPopupOpen) return;
        if (openMarkerId === externalMarkerId) return;
        // Another marker opened (or openMarkerId was cleared) — close ours
        setMarkerState((prev) => ({ ...prev, isPopupOpen: false }));
    }, [openMarkerId]);

    // Listen for map clicks to close popover
    useEffect(() => {
        if (!map || !markerState.isPopupOpen) return;

        const listener = map.addListener('click', () => {
            closePopup();
        });

        return () => {
            google.maps.event.removeListener(listener);
        };
    }, [map, markerState.isPopupOpen]);

    useEffect(() => {
        let isMounted = true;

        const createMarker = async () => {
            if (!map || !window.google || !isMounted) return;
            const { AdvancedMarkerElement } = (await google.maps.importLibrary(
                'marker'
            )) as {
                AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement;
            };

            if (!markerRef.current) {
                markerRef.current = new AdvancedMarkerElement({
                    map,
                    position: new google.maps.LatLng(position),
                    content: markerContainerRef.current,
                });

                markerRef.current.id = externalMarkerId || crypto.randomUUID();

                if (onMarkerClick || popoverContent) {
                    markerRef.current.element?.addEventListener(
                        'mouseenter',
                        handleMarkerMouseEnter
                    );
                    markerRef.current.element?.addEventListener(
                        'mouseleave',
                        handleMarkerMouseLeave
                    );
                    markerRef.current.addListener('click', handleMarkerClick);
                }
            } else {
                markerRef.current.map = map;
                markerRef.current.position = new google.maps.LatLng(position);
            }
        };

        createMarker();
        return () => {
            isMounted = false;
            if (markerRef.current) {
                markerRef.current.map = null;
                markerRef.current = null;
            }
        };
    }, [map, position]);

    const handleMarkerMouseEnter = () => {
        onMarkerHover?.('enter');
        setMarkerState((prev) => ({ ...prev, isMarkerHovered: true }));
    };

    const handleMarkerMouseLeave = () => {
        if (!markerStateRef.current.isPopupOpen) {
            onMarkerHover?.('exit');
        }
        setMarkerState((prev) => ({ ...prev, isMarkerHovered: false }));
    };

    const handleMarkerClick = () => {
        if (markerStateRef.current.isPopupOpen) {
            closePopup();
        } else {
            openPopup();
            onMarkerClick?.();
        }
    };

    const openPopup = () => {
        openPopupRef.current();
    };

    const closePopup = () => {
        setOpenMarkerId(undefined);
        setMarkerState((prev) => ({ ...prev, isPopupOpen: false }));
    };

    const resolvedMarkerContent =
        typeof markerContent === 'function'
            ? markerContent({
                  isPopupOpen: markerState.isPopupOpen,
                  isHovered:
                      markerState.isMarkerHovered ||
                      isExternallyHovered ||
                      false,
              })
            : markerContent;

    return (
        <>
            <BaseMarkerPortal
                markerContent={resolvedMarkerContent}
                markerContainerRef={markerContainerRef}
            />
            <BasePopOverPortal
                isOpen={markerState.isPopupOpen}
                markerContainerRef={markerContainerRef}
                popoverAnchor={popoverAnchor}
                popoverContent={popoverContent}
                position={position}
            />
        </>
    );
};

interface BaseMarkerPortalProps {
    markerContent: React.ReactNode;
    markerContainerRef: React.MutableRefObject<HTMLDivElement>;
}

const BaseMarkerPortal = ({
    markerContent,
    markerContainerRef,
}: BaseMarkerPortalProps) => {
    return createPortal(markerContent, markerContainerRef.current);
};

interface BasePopOverPortalProps {
    isOpen: boolean;
    markerContainerRef: React.MutableRefObject<HTMLDivElement>;
    popoverAnchor: Anchors;
    popoverContent: React.ReactNode;
    position: google.maps.LatLng | google.maps.LatLngLiteral;
}

const BasePopOverPortal = ({
    isOpen,
    markerContainerRef,
    popoverAnchor,
    popoverContent,
    position,
}: BasePopOverPortalProps) => {
    const { map } = useMap();
    return (
        <When condition={isOpen}>
            <PopupOverlay
                map={map}
                position={position}
                anchor={popoverAnchor}
                markerElement={markerContainerRef.current}
            >
                {popoverContent}
            </PopupOverlay>
        </When>
    );
};
